import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { BLACK_FRIDAY_CAMPAIGN_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of `specs.symphony.BlackFridayCampaign` — the campaign's one switch, and the SAME spec
 * the harness conducts to decide whether the maestro carries the Black Friday skill, and the one both
 * phones conduct for themselves.
 *
 * This is only the FLAG half. Whether a band actually shows is `use-black-friday.ts`, which also
 * requires the credits read to have settled.
 *
 * FAILS CLOSED to off — a campaign that has not ramped must never flash at everyone, and an outage
 * must not start pitching one. Same bound and same shape as the sibling gates.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS off. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function blackFridayCampaignEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(BLACK_FRIDAY_CAMPAIGN_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/black-friday-campaign`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS off.
      return false;
    }
  })();
  return cached;
}

/** Starts false, resolves in an effect — so SSR and the first paint never flash a band. */
export function useBlackFridayCampaignEnabled(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let live = true;
    void blackFridayCampaignEnabled().then((value) => {
      if (live) setOn(value);
    });
    return () => {
      live = false;
    };
  }, []);
  return on;
}
